import React, { useEffect, useRef, useState } from "react";
import { canAssignProvider, isClosedBooking, isPlaceholderName, needsAssignment, realProviderNames } from "../../utils/bookingAssignment";
import ProviderAssignCell from "../../components/Dashboard/ProviderAssignCell";
import AssignProviderModal from "./AssignProviderModal";
import { useNavigate, useParams } from "react-router-dom";
import * as apicall from "../../components/Apicall";
import { LuClipboardCheck, LuClock } from "react-icons/lu";
import { HiOutlineUsers } from "react-icons/hi2";
import Skeleton from "../../components/Loader/Skeleton";
import { Form, Offcanvas } from "react-bootstrap";
import NewAppointment from "../../components/Appointment/NewAppointment";
import { MdArrowBack, MdInfoOutline } from "react-icons/md";
import { FaRegCalendarAlt } from "react-icons/fa";
import DatePicker from "react-datepicker";
import { MultiSelect } from "primereact/multiselect";
import ExcelIcon from "../../assets/images/vscode-icons_file-type-excel.svg";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";
import * as constants from "../../constants/Constants";
import { toTitleCase } from "../../utils/textUtils";
import useViewport from "../../hooks/useViewport";
import * as inventoryService from "../../services/inventoryService";
import * as invoiceService from "../../services/invoiceService";
import InvoiceCancelDialog from "../../components/Invoice/InvoiceCancelDialog";
import InvoiceDateDialog from "../../components/Invoice/InvoiceDateDialog";
import { getPersonaForRole, PERSONA } from "../../constants/roles";
import { toast } from "react-toastify";
import { getInvoiceAssets } from "../../services/customerAssetService";
import { buildInvoiceHtml, splitIntoOfferings, bookingGroupId } from "../../utils/invoiceHtml";
import { fetchBookingStatusesForIndustry, isCompletedStatusName } from "../../utils/bookingStatus";
import { getBookingLabel } from "../../utils/industryLabels";
import { formatStepAssignments } from "../Vendor_bookings/workflow/workflowAssignments";

const bookingStatusConfig = {
  Completed: { bg: "#E6F9F0", color: "#27AE60", border: "#27AE60" },
  "In Progress": { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE" },
  Cancelled: { bg: "#FFF3F3", color: "#C0335A", border: "#C0335A" },
};

// Normalizes a status name down to letters only so "Check Out", "Checked
// Out", "check-out" etc. all compare equal — same helper/approach used in
// Dashboard.jsx's tile counts, kept local here since these two pages don't
// share a util module.
const normStatus = (s) => (s || "").toString().toLowerCase().replace(/[^a-z]/g, "");

// Does this row's booking_status belong to the given overview tile key?
const rowMatchesTile = (bookingStatus, tileKey) => {
  const n = normStatus(bookingStatus);
  switch (tileKey) {
    case "checkedin":
      return n.includes("checkin") || n.includes("checkedin");
    case "checkedout":
      return n.includes("checkout") || n.includes("checkedout") || n === "completed";
    case "noshow":
      return n.includes("noshow");
    case "cancelled":
      return n === "cancelled" || n === "canceled";
    case "completed":
      return n === "completed";
    case "inprogress":
      return bookingStatus === "In Progress";
    default:
      return true;
  }
};
const paymentStatusConfig = {
  Paid: { bg: "#E6F9F0", color: "#27AE60", border: "#27AE60" },
  Partial: { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE" },
  Pending: { bg: "#FFFBEB", color: "#D97706", border: "#FDE68A" },
};

const SortIcon = ({ col, sortCol, sortDir }) => {
  const active = sortCol === col;
  return (
    <span
      style={{
        display: "inline-flex",
        flexDirection: "column",
        marginLeft: "4px",
        verticalAlign: "middle",
      }}
    >
      <span
        style={{
          fontSize: "8px",
          lineHeight: 1,
          color: active && sortDir === "asc" ? "#C0335A" : "#D1D5DB",
        }}
      >
        ▲
      </span>
      <span
        style={{
          fontSize: "8px",
          lineHeight: 1,
          color: active && sortDir === "desc" ? "#C0335A" : "#D1D5DB",
        }}
      >
        ▼
      </span>
    </span>
  );
};

const StatusPill = ({ label, configMap }) => {
  const cfg = configMap[label] || {
    bg: "#F3F4F6",
    color: "#6B7280",
    border: "#D1D5DB",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: "600",
        background: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {label || "-"}
    </span>
  );
};

const currencySymbols = {
  INR: "₹",
  AED: "AED",
  USD: "$",
  EUR: "€",
};

function DashboardDetailed() {
  let { date } = useParams();
  const contentRef = useRef();
  // "vendorProfile" is refreshed by Navbar on every mount (ezpMyProfile);
  // "userDetails" is only written by specific setup-flow code paths and can
  // stay stale for a while, which was routing clinic vendors' booking clicks
  // to the generic Bookings edit page instead of the Appointment sidepanel.
  // let userDetailsRaw = localStorage.getItem("vendorProfile");
  // const userDetails =
  //   userDetailsRaw && userDetailsRaw !== "undefined" ? JSON.parse(userDetailsRaw) : null;

  // let d1 = date?.toLocaleDateString("en-GB")?.split(/[\/\-\.]/);
  // console.log("d1---", d1);
  // let d2 = `${d1[2]}-${d1[1]}-${d1[0]}`;
  // console.log("d2--", d2);
  const { isPhone: isMobile } = useViewport();
  // const today = new Date();
  const [dateRange, setDateRange] = useState([date, date]);
  const [startDate, endDate] = dateRange;
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const lastRequestId = useRef(0);
  const [orderCounts, setOrderCounts] = useState({
    total: 0,
    completed: 0,
    progress: 0,
    unassigned:0,
    cancelled: 0,
    checkedIn: 0,
    checkedOut: 0,
    noShow: 0,
  });
  // Which overview tile is active — filters the table below by status.
  // null/"all" = no filter (Today's Appointment / Total tile).
  const [activeTileStatus, setActiveTileStatus] = useState(null);
  // The booking whose service provider is being assigned from the list (null = dialog closed)
  const [assigning, setAssigning] = useState(null);
  const [fullData, setFullData] = useState([]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [allPartner, setAllPartner] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState([]);

  const [allProvider, setAllProvider] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState([]);

  const [allStatus, setAllStatus] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);

  const [allPayment, setAllPayment] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState([]);
  const [vendorData, setVendorData] = useState();
  const [downloadInv, setDownloadInv] = useState([]);
  const [showCancelInvoice, setShowCancelInvoice] = useState(false);
  // Generate Invoice popup (asks for the invoice date).
  const [showInvoiceDate, setShowInvoiceDate] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [currency, setCurrency] = useState("AED");
  const [sortCol, setSortCol] = useState("service_date");
  const [sortDir, setSortDir] = useState("desc");
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
const[industry,setIndustry]=useState([])
  const [showAppointmentPanel, setShowAppointmentPanel] = useState(false);
  const [appointmentPanelMode, setAppointmentPanelMode] = useState("new");
  const [appointmentGroupId, setAppointmentGroupId] = useState(null);

  const closeAppointmentPanel = () => {
    setShowAppointmentPanel(false);
    setAppointmentGroupId(null);
    getBookingByDate();
  };

  const currencyCode = currency || "INR";
  const symbol = currencySymbols[currencyCode] || currencyCode;

  const goToPreviousMonth = () => {
    const prev = new Date(currentMonth);
    prev.setMonth(prev.getMonth() - 1);
    setCurrentMonth(prev);
  };

  useEffect(() => { setPage(1); }, [searchQuery]);

  const goToNextMonth = () => {
    const next = new Date(currentMonth);
    next.setMonth(next.getMonth() + 1);
    setCurrentMonth(next);
  };

  useEffect(() => {
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
      setCurrency(userDetails?.currency);
    } catch (e) {
      // userDetails is not JSON (e.g. the text "undefined") - no currency yet.
      setCurrency(undefined);
    }
  }, []);

  useEffect(() => {
    getAllDropdown();
  }, []);

  useEffect(() => {
    getBookingByDate();
  }, [
    dateRange,
    selectedPartner,
    selectedProvider,
    selectedStatus,
    selectedPayment,
  ]);
   const fetchVendorProfile = async () => {
        try {
          const [response] = await Promise.all([
            apicall.get("ezpMyProfile"),
          ]);

          if (response.status === 200 && response.success) {
            const vendorData = response.data?.[0];
            setIndustry(vendorData)
            // Prefer ezpMyProfile's own currency over the localStorage
            // "userDetails" read below — that key is only written by certain
            // setup flows and was showing up undefined here for profiles
            // that never went through them.
            if (vendorData?.currency) {
              setCurrency(vendorData.currency);
            }
          }
        } catch (error) {
          console.error("Error fetching vendor profile:", error);
        } finally {
          setLoading(false);
        }
      };

  useEffect(() => {
    fetchVendorProfile();
  }, []);
  const isClinicService = industry?.industry === 1;
  const isSportsService = industry?.industry === 7;
  const isCarService = industry?.industry === 8;
  const isSalonService = industry?.industry === 9;

  const crewLabel = isClinicService ? "Doctor" : isSportsService ? "Coach" : isCarService || isSalonService ? "Service Provider" : "Crew";
  const PatientLabel = isClinicService ? "Patient Name" : isSportsService ? "Player Name" : "Customer Name";
  const serviceLabel = isClinicService ? "Specialization" : "Service";
  const serviceCategoryLabel = isClinicService ? "Specialization" : "Service category";
  const serviceTypeLabel = isClinicService ? "Specialization Type" : "Service Type";
  const serviceDateLabel = isClinicService ? "Appointment Date" : "Service Date";
  const serviceTimeLabel = isClinicService ? "Appointment Time" : "Service Time";
  const bookingStatusLabel = isClinicService ? "Appointment Status" : "Booking Status";
  const bookingIdLabel = isClinicService ? "Appointment ID" : "Booking ID";

  console.log("downloadInv---", downloadInv);

  let headers = [
    "S.NO",
    "Invoice",
    bookingIdLabel,
    "Origin ID",
    "Profile",
    PatientLabel,
    // ...(isClinicService ? ["Patient Name"] : ["Customer Name"]),
    "Phone Number",
    crewLabel,
    serviceCategoryLabel,
    ...(!isClinicService ? ["Service Sub category"] : []),
    serviceTypeLabel,
    serviceDateLabel,
    serviceTimeLabel,
    `Discount (${symbol})`,
    `Tax (${symbol})`,
    `Total (${symbol})`,
    "Total paid amount",
    "Balance due",
    bookingStatusLabel,
    "Payment Status",
    "Added Item Amount",
  ];

  // Optional bookable resource (bay, van, room, court ...). The column only
  // appears once a booking in view actually holds one, so the table is
  // unchanged for anyone who does not use resources.
  const resourceColumnLabel = isClinicService ? "Room" : "Resource";
  const hasResourceData = (fullData || []).some((row) => row?.resource_names);
  if (hasResourceData) {
    headers.splice(headers.indexOf(crewLabel) + 1, 0, resourceColumnLabel);
  }

  let exportheaders = [
    "S.NO",
    bookingIdLabel,
    "Origin ID",
    "Profile",
    PatientLabel,
    "Phone Number",
    crewLabel,
    serviceCategoryLabel,
    ...(!isClinicService ? ["Sub category"] : []),
    "Type",
    serviceDateLabel,
    serviceTimeLabel,
    "Discount",
    "Tax",
    "Total",
    bookingStatusLabel,
    "Payment Status",
  ];

  let invoiceheaders = [
    bookingIdLabel,
    "Product/Services",
    "Discount",
    "Tax",
    "Total",
  ];

  let mappingColumns = {
    "S.NO": {
      name: "s_no",
      width: "4rem",
    },
    Invoice: {
      name: "invoice_id",
      width: "4rem",
    },
    [bookingIdLabel]: {
      name: "display_booking_id",
      width: "7rem",
    },
    "Origin ID": {
      name: "org_id",
      width: "7rem",
    },
    Profile: {
      name: "partner_name",
      width: "10rem",
    },
    [PatientLabel]: {
  name: "user_name",
  width: "10rem",
},
    "Phone Number": {
      name: "phone_number",
      width: "10rem",
    },
    [crewLabel]: {
      name: "provider_names",
      width: "10rem",
    },
    [resourceColumnLabel]: {
      name: "resource_names",
      width: "10rem",
    },
    [serviceCategoryLabel]: {
      name: "service_name",
      width: "10rem",
    },
    "Service Sub category": {
      name: "service_type_name",
      width: "12rem",
    },
    [serviceTypeLabel]: {
      name: "service_offering_name",
      width: "12rem",
    },
    [serviceDateLabel]: {
      name: "service_date",
      width: "10rem",
    },
    [serviceTimeLabel]: {
      name: "service_time",
      width: "10rem",
    },
    [`Discount (${symbol})`]: {
      name: "discount",
      width: "10rem",
    },
    [`Tax (${symbol})`]: {
      name: "tax",
      width: "10rem",
    },
    [`Total (${symbol})`]: {
      name: "total",
      width: "10rem",
    },
    "Total paid amount": {
      name: "total_paid_amount",
      width: "12rem",
    },
    "Balance due": {
      name: "balance_due",
      width: "10rem",
    },
    [bookingStatusLabel]: {
      name: "booking_status",
      width: "10rem",
    },
    "Payment Status": {
      name: "payment_status",
      width: "10rem",
    },
    "Added Item Amount": {
      name: "item_prices",
      width: "15rem",
    },
  };


  const formatDate = (d1) => {
    if (!d1) return null;
    let date = new Date(d1);
    date.setHours(0, 0, 0, 0);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getAllDropdown = async () => {
    try {
      // Fetch industry fresh from ezpMyProfile rather than trusting local
      // state, which is populated by a separate, independently-timed fetch.
      const profileRes = await apicall.get("ezpMyProfile");
      const profileIndustry = profileRes?.data?.[0]?.industry;

      let [
        vendor_api,
        partners_api,
        providers_api,
        allStatus,
        paymentstatus_api,
      ] = await Promise.all([
        apicall.get(`get_ezp_vendor_data`),
        apicall.get(`partners`),
        apicall.get(`providers`),
        fetchBookingStatusesForIndustry(apicall.get, profileIndustry),
        apicall.get(`get_ezp_payment_status`),
      ]);

      
      let pat = partners_api?.data?.map((ele) => {
        return {
          id: ele?.id,
          name: ele?.partner_name,
        };
      });
      setVendorData(vendor_api?.data);
      setAllPartner(pat);
      setAllProvider(providers_api?.data);
      setAllStatus(allStatus);
      setAllPayment(paymentstatus_api?.data);
    } catch (e) {
      console.log("err---", e);
    }
  };

  const getBookingByDate = async () => {
    try {
      if (!startDate) return;
      setLoading(true);
      let currentId = ++lastRequestId.current;
      let startStr = formatDate(startDate);
      let endStr = formatDate(endDate);
      if (!endStr) endStr = startStr;
      // console.log("startstr---", startStr, "endStr------", endStr);

      let pat1 = selectedPartner?.map((ele) => ele?.id);
      let pro1 = selectedProvider?.map((ele) => ele?.id);
      let stat1 = selectedStatus?.map((ele) => ele?.id);
      let pay1 = selectedPayment?.map((ele) => ele?.id);

      let apiURL =
        startStr === endStr
          ? `get_ezp_booking?date=${startStr}`
          : `get_ezp_booking?startDate=${startStr}&&endDate=${endStr}`;
      // console.log("apiUerl--", apiURL);
      // apiURL += `&&partFeditner=${pat1}&&provider=${pro1}&&status=${stat1}&&payment=${pay1}&limit=${limit}&page=${page}`;
      apiURL += `&&partner=${pat1}&&provider=${pro1}&&status=${stat1}&&payment=${pay1}&page=${page}`;

      const get_booking_data = await apicall.get(apiURL);

      if (currentId !== lastRequestId.current) return;

      // console.log("get_booking_data-----", get_booking_data);
      let allData = get_booking_data?.data;
      allData?.map((ele) => {
        ele.invoice = false;
        return ele;
      });

      setFullData(allData);
      setSelectAll(false);
      // console.log("allData-------", get_booking_data);

      // setTotalPages(get_booking_data?.pagination?.total_count || 0);

      const counts = {
        total: allData.length,
        // Clinic bookings default to "Checked In" (status id 5) rather than
        // the generic "In Progress" status, but should still count as active.
        progress: allData.filter(
          (item) =>
            item.booking_status === "In Progress" ||
            (isClinicService && Number(item.status) === 5)
        ).length,
        completed: allData.filter((item) => item.booking_status === "Completed")
          .length,
        cancelled: allData.filter((item) => rowMatchesTile(item.booking_status, "cancelled"))
          .length,
        unassigned: allData.filter(needsAssignment).length,
        // Clinic-only tiles
        checkedIn: allData.filter((item) => rowMatchesTile(item.booking_status, "checkedin")).length,
        checkedOut: allData.filter((item) => rowMatchesTile(item.booking_status, "checkedout")).length,
        noShow: allData.filter((item) => rowMatchesTile(item.booking_status, "noshow")).length,
      };
      setOrderCounts(counts);
      setLoading(false);
    } catch (e) {
      console.log("err----", e);
    }
  };

  const newBookingNavigate = (row) => {
    if (isClinicService) {
      // Clinic bookings edit in-place via a sidepanel here, instead of
      // redirecting to the Dashboard page (which caused a page-shrink flash).
      setAppointmentPanelMode("edit");
      setAppointmentGroupId(row?.display_booking_id || null);
      setShowAppointmentPanel(true);
    } else {
      navigate(`/vendor_bookings/${row?.display_booking_id}`);
    }
  };

  const onBackClick = (data) => {
    navigate(`/dashboard`);
  };

  const formatDateDisplay = (date) => {
    if (!date) return "";

    const d = new Date(date);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    // return `${
    //   months[d.getUTCMonth()]
    // } ${d.getUTCDate()}, ${d.getUTCFullYear()}`;

    // let s1 = new Date(date);
    // s1.setHours(0, 0, 0, 0);

    // let d1 = date?.toLocaleDateString("en-GB")?.split(/[\/\-\.]/);
    // console.log("d1---", d1);
    // let d2 = `${d1[2]}-${d1[1]}-${d1[0]}`;
    // console.log("d2--", d2);

    // return d2;
    // return "";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  const formatAmount = (val) => {
    if (
      val === null ||
      val === undefined ||
      val === "" ||
      val === "undefined"
    ) {
      return "0.00";
    }
    return Number(val).toFixed(2);
  };
  const handleDateChange = (data) => {
    try {
      // console.log("data-----", data);
      let [s, e] = data;

      let d1 = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(s.getDate()).padStart(2, "0")}`;

      let d2 = e
        ? `${e.getFullYear()}-${String(e.getMonth() + 1).padStart(
            2,
            "0"
          )}-${String(e.getDate()).padStart(2, "0")}`
        : null;

      // console.log("d1-----", d1, d2);

      setDateRange(data);
    } catch (e) {
      console.log("error--", e);
    }
  };

  async function clearAll() {
    setSelectedPartner([]);
    setSelectedProvider([]);
    setSelectedStatus([]);
    setSelectedPayment([]);
  }

  const handleSort = (colKey) => {
    setSortDir((prev) =>
      sortCol === colKey ? (prev === "asc" ? "desc" : "asc") : "asc"
    );
    setSortCol(colKey);
  };

  const toggleRow = (groupId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const numericCols = new Set([
    "total",
    "tax",
    "discount",
    "total_paid_amount",
    "balance_due",
    "price",
  ]);
  const dateCols = new Set(["service_date"]);
  const nonSortableCols = new Set([
    "s_no",
    "invoice_id",
    "item_prices",
    "provider_names",
  ]);

  const tileFilteredData = activeTileStatus
    ? (fullData || []).filter((row) =>
        activeTileStatus === "unassigned" ? needsAssignment(row) : rowMatchesTile(row.booking_status, activeTileStatus)
      )
    : (fullData || []);

  const searchFilteredData = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return tileFilteredData;
    return tileFilteredData.filter((row) =>
      [
        row.org_id,
        row.user_name,
        row.phone_number,
        row.provider_names,
        formatStepAssignments(row),
        row.service_name,
        row.service_type_name,
        row.service_offering_name,
      ].some((v) => String(v || "").toLowerCase().includes(q))
    );
  })();

  const totalPages = Math.ceil((searchFilteredData.length || 0) / limit);

  const sortedDisplayData = [...searchFilteredData]
    .sort((a, b) => {
      const av = a[sortCol],
        bv = b[sortCol];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      let cmp;
      if (dateCols.has(sortCol)) {
        cmp = new Date(av) - new Date(bv);
      } else if (numericCols.has(sortCol)) {
        cmp = parseFloat(av) - parseFloat(bv);
      } else {
        cmp = String(av).localeCompare(String(bv));
      }
      return sortDir === "asc" ? cmp : -cmp;
    })
    .slice((page - 1) * limit, page * limit);

  const handleSelectAll = () => {
    const next = !selectAll;
    setSelectAll(next);
    // Only completed bookings can be invoiced, so only they are ticked.
    const updated = fullData.map((row) => ({
      ...row,
      invoice: row.invoice_id ? false : next && isCompletedStatusName(row.booking_status),
    }));
    setFullData(updated);
    setDownloadInv(next ? updated.filter((r) => r.invoice) : []);
  };

  const escapeCSVValue = (value) => {
    // Convert to string
    const stringValue = String(value);

    // If value contains comma, quote, or newline, wrap in quotes
    // and escape any existing quotes by doubling them
    if (
      stringValue.includes(",") ||
      stringValue.includes('"') ||
      stringValue.includes("\n")
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  };

  const convertTo12Hour = (time24) => {
    const [hourStr, minute] = time24.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12; // Convert 0 to 12
    return `${hour}:${minute} ${ampm}`;
  };
  const exportColumnMap = {
    "Sub category": "service_type_name",
    Type: "service_offering_name",
    Discount: "discount",
    Tax: "tax",
    Total: "total",
  };

  const exportToCSV = () => {
    const exportColumnMap = {
      "Sub category": "service_type_name",
      Type: "service_offering_name",
      Discount: "discount",
      Tax: "tax",
      Total: "total",
    };

    // Convert data to CSV format
    const headers = exportheaders.join(",");
    // const rows = fullData.map((row) => Object.values(row).join(","));
    const rows = fullData.map((row, idx) => {
      let ans = [];

      for (let eachData of exportheaders) {
        if (eachData === "S.NO") {
          ans.push(idx + 1);
        } else {
          const colKey =
            mappingColumns[eachData]?.name || exportColumnMap[eachData];
          ans.push(escapeCSVValue(row[colKey]));
        }
      }
      return ans.join(",");
    });
    // console.log("rows-----", fullData[0], headers, rows);

    const csv = [headers, ...rows].join("\n");

    // Create blob and download
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
a.download = isClinicService
  ? `Appointments_${new Date().toISOString().split("T")[0]}.csv`
  : `bookings_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  function passFun() {}

  // const downloadInvoice = async () => {
  //   setLoading(true);

  //   try {
  //     const html2pdf = (await import("html2pdf.js")).default;

  //     const element = contentRef.current;
  //     const opt = {
  //       margin: [10, 10, 10, 10],
  //       filename: `invoice_${new Date().toISOString().split("T")[0]}.pdf`,
  //       image: { type: "jpeg", quality: 0.98 },
  //       html2canvas: {
  //         scale: 1.5,
  //         useCORS: true,
  //         letterRendering: true,
  //         allowTaint: false,
  //       },
  //       jsPDF: {
  //         unit: "mm",
  //         format: "a4",
  //         orientation: "portrait",
  //         compress: true,
  //       },
  //       pagebreak: { mode: ["avoid-all", "css", "legacy"] },
  //     };

  //     await html2pdf().set(opt).from(element).save();
  //   } catch (error) {
  //     console.error("PDF generation failed:", error);
  //     alert("Failed to generate PDF");
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const getAmount = (label) => {
    let s = 0;

    let copydownloadInv = JSON.parse(JSON.stringify(downloadInv));
    copydownloadInv?.map((ele) => {
      let s1 = parseFloat(ele[label]) || 0;
      s = parseFloat(parseFloat(s) + s1);
    });
    return s;
  };

  const sortedData = [...downloadInv].sort((a, b) => {
    return new Date(a.service_date) - new Date(b.service_date);
  });
  const total = Number(getAmount("total") || 0);
  const commissionPercent = Number(downloadInv?.[0]?.commission || 0);

  const correctCommissionAmount = (total * commissionPercent) / 100;
  const hasOriginId = sortedData?.some(
    (ele) => ele?.org_id && ele?.org_id !== ""
  );
  // HTML-building logic lives in src/utils/invoiceHtml.js (buildInvoiceHtml)
  // now, shared with the single-booking "Generate Invoice" button in
  // Bookings.jsx, so both stay visually identical instead of drifting.
  // invoiceDate: 'YYYY-MM-DD' from the Generate Invoice popup.
  const downloadInvoice = async (invoiceDate) => {
    setLoading(true);

    try {
      const partsByBookingId = {};
      await Promise.all(
        (sortedData || []).map(async (ele) => {
          try {
            const res = await inventoryService.getBookingParts(ele.booking_id);
            partsByBookingId[ele.booking_id] = Array.isArray(res?.data) ? res.data : [];
          } catch (e) {
            partsByBookingId[ele.booking_id] = [];
          }
        })
      );

      const assetsByBookingId = await getInvoiceAssets(
        (sortedData || []).map((ele) => ele.booking_id)
      );

      // A list row with several offerings has their names comma-joined and
      // one combined total/tax. Fetch the booking's per-offering rows so the
      // invoice shows each offering on its own line with its own amounts.
      // Splitting is skipped when several selected rows share a group (those
      // rows are already split, and splitting again would repeat the
      // offerings) - but the details are still fetched for every group, to
      // fill in the customer's address/phone for "Bill to".
      const groupCounts = {};
      (sortedData || []).forEach((ele) => {
        const gid = bookingGroupId(ele);
        groupCounts[gid] = (groupCounts[gid] || 0) + 1;
      });
      const offeringsByGroup = {};
      await Promise.all(
        Object.keys(groupCounts)
          .filter((gid) => gid && gid !== "null")
          .map(async (gid) => {
            try {
              const res = await apicall.get(`get_ezp_booking_by_id?group_id=${gid}`);
              offeringsByGroup[gid] = Array.isArray(res?.data) ? res.data : [];
            } catch (e) {
              offeringsByGroup[gid] = [];
            }
          })
      );

      const normalizedBookings = (sortedData || []).flatMap((ele) => {
        const itemNames = (ele?.item_names || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        const itemPrices = (ele?.item_prices || "").split(",");
        const gid = bookingGroupId(ele);
        const details = offeringsByGroup[gid]?.[0] || {};
        const row = {
          ...ele,
          // List rows can come back without the customer's address/phone;
          // fall back to the booking's own details for "Bill to".
          ...Object.fromEntries(
            ["house_no", "area", "landmark", "location", "state", "phone_number"]
              .filter((k) => !ele?.[k] && details[k])
              .map((k) => [k, details[k]])
          ),
          addedItems: itemNames.map((name, index) => ({ name, price: itemPrices[index] })),
        };
        return groupCounts[gid] === 1
          ? splitIntoOfferings(row, offeringsByGroup[gid])
          : [row];
      });

      const { htmlContent, booking_ids } = buildInvoiceHtml({
        bookings: normalizedBookings,
        partsByBookingId,
        assetsByBookingId,
        vendorData,
        currency,
        isClinicService,
        invoiceDate,
      });

      const data = { booking_ids, htmlContent, invoice_date: invoiceDate };
      await apicall.generate_pdf(data, "generate_pdf");
      setDownloadInv([]);
      await getBookingByDate();
      setLoading(false);
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF");
    } finally {
      setLoading(false);
    }
  };

  // console.log("vendorData---", vendorData);

  function close() {
    // console.log("close.......");
    setOpen(false);
  }

  function checkBoxInvoice(data) {
    try {
      // Toggle only the exact row that was clicked (by reference) — rows are
      // selected independently even when they share a group_id.
      // A booking that is neither invoiced nor completed cannot be invoiced yet.
      const invoiced = !!(data.invoice_ref_id || data.invoice_id);
      if (!invoiced && !isCompletedStatusName(data.booking_status)) return;
      const updated = fullData.map((row) =>
        row === data ? { ...row, invoice: !row.invoice } : row
      );

      setDownloadInv(updated.filter((ele) => ele.invoice));
      setFullData(updated);
    } catch (e) {
      console.log("error---", e);
    }
  }

  const getPageNumbers = () => {
    const pageNumbers = [];
    const totalPagesToShow = 5;
    const halfWay = Math.floor(totalPagesToShow / 2);

    if (totalPages <= totalPagesToShow) {
      // If total pages are less than or equal to totalPagesToShow, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);

      let startPage = Math.max(2, page - halfWay);
      let endPage = Math.min(totalPages - 1, page + halfWay);

      // Adjust start and end if we're near the beginning or end
      if (page <= halfWay + 1) {
        endPage = totalPagesToShow - 1;
      } else if (page >= totalPages - halfWay) {
        startPage = totalPages - totalPagesToShow + 2;
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pageNumbers.push("...");
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }

      // Always show last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };
  const downloadPDF = async () => {
    const htmlContent = `
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #333; }
          </style>
        </head>
        <body>
          <h1>My Report</h1>
          <p>This is HTML content rendered as PDF.</p>
        </body>
      </html>
    `;

    const response = await apicall.generate_pdf(htmlContent, "generate_pdf");

    // console.log("response---", response);
    // const blob = await response.blob();
    // const url = URL.createObjectURL(blob);

    // // Trigger download
    // const a = document.createElement("a");
    // a.href = url;
    // a.download = "document.pdf";
    // a.click();
    // URL.revokeObjectURL(url);
  };

  // ── Invoiced rows ────────────────────────────────────────────────────────
  // Admin / vendor roles may select invoiced bookings to cancel their invoice
  // (the API enforces the same roles). A selection is either all not-yet-invoiced
  // (Download Invoice, as before) or all invoiced (Cancel Invoice) - never mixed.
  const canCancelInvoices = getPersonaForRole(apicall.getRole()) === PERSONA.ADMIN_VENDOR;
  // Same roles the API lets assign a service provider (admin, vendor, support)
  const canAssignProviders = [2, 3, 6].includes(Number(apicall.getRole()));
  const isInvoicedRow = (r) => !!(r.invoice_ref_id || r.invoice_id);
  // Invoiced rows are locked for people who cannot cancel invoices; the rest only
  // unlock once the booking is completed.
  const invoiceBoxLocked = (r) =>
    isInvoicedRow(r) ? !canCancelInvoices : !isCompletedStatusName(r.booking_status);
  const invoicedSelected = downloadInv.filter(isInvoicedRow);
  const pendingSelected = downloadInv.filter((r) => !isInvoicedRow(r));
  const cancelMode = invoicedSelected.length > 0 && pendingSelected.length === 0;
  const mixedSelection = invoicedSelected.length > 0 && pendingSelected.length > 0;
  const rowKey = (r) => String(r.group_id ?? `b${r.booking_id}`);

  // One invoice can cover several bookings: load every invoice behind the
  // selection so the dialog can list the bookings that will be cancelled with it.
  const loadInvoiceImpacts = async () => {
    const refs = [...new Set(invoicedSelected.map((r) => r.invoice_ref_id).filter(Boolean))];
    if (!refs.length) throw new Error("These bookings have no invoice record to cancel");
    const results = await Promise.all(refs.map((id) => invoiceService.getInvoiceImpact({ invoiceId: id })));
    return results.map((res) => {
      const { invoice, bookings, has_payments } = res.data;
      return {
        id: invoice.id,
        number: invoice.invoice_number,
        currency: invoice.currency || currency,
        hasPayments: has_payments,
        items: bookings.map((b) => ({
          key: String(b.group_id ?? `b${b.booking_id}`),
          label: b.display_booking_id || `Booking #${b.booking_id}`,
          detail: [b.customer_name, b.service_date].filter(Boolean).join(" - "),
          amount: b.total,
          paid: b.paid_amount,
        })),
      };
    });
  };

  const cancelSelectedInvoices = async (reason, impacts) => {
    for (const imp of impacts) {
      const res = await invoiceService.cancelInvoice(imp.id, reason);
      if (res?.status !== 200) throw new Error(res?.message || `Could not cancel ${imp.number}`);
    }
    toast.success(impacts.length > 1 ? "Invoices cancelled" : `Invoice ${impacts[0].number} cancelled`);
    setShowCancelInvoice(false);
    setDownloadInv([]);
    setSelectAll(false);
    await getBookingByDate();
  };

  // console.log("downloadInv---", downloadInv);

  return (
    <>
      <div onClick={close}>
        <div style={{ display: "none" }}>
          <div
            ref={contentRef}
            style={{
              padding: "4px",
              paddingTop: "1rem",
              margin: "0 auto",
              boxSizing: "border-box",
            }}
            className="bg-white rounded-lg shadow-lg p-8"
          >
            <div className="d-flex justify-content-center">
              <h3 className=" p-2 py-1 font-bold text-center mb-8 text-gray-800">
                Tax Invoice
              </h3>
            </div>
            <header className="d-flex align-items-start gap-4 mb-8">
              {vendorData?.[0]?.profile_image ? (
                <div>
                  <img
                    src={vendorData?.[0]?.profile_image}
                    // crossOrigin="anonymous"
                    alt="profile"
                  />
                </div>
              ) : (
                <div
                  style={{ color: "#fff", backgroundColor: "rgb(223, 48, 94)" }}
                  className="text font-bold p-4 text-blue-600"
                >
                  {vendorData?.[0]?.first_name}
                </div>
              )}

              <div>
                <h5 className="text-gray-600">{vendorData?.[0]?.first_name}</h5>
                <div className="text-gray-600">{vendorData?.[0]?.city}</div>
                <div className="text-gray-600">{vendorData?.[0]?.state}</div>
                <div className="text-gray-600">
                  {vendorData?.[0]?.country_name}
                </div>
              </div>
              <div>
                <div className="text-gray-600">{vendorData?.[0]?.email}</div>
                <div className="text-gray-600">
                  {vendorData?.[0]?.phone_number}
                </div>
              </div>
            </header>

            <div className="d-flex mt-4">
              <div style={{ width: "10%" }} className="invoiceHead">
                {bookingIdLabel}
              </div>
              <div style={{ width: "60%" }} className="invoiceHead">
                Product/Services
              </div>
              <div style={{ width: "30%" }} className="invoiceHead">
                Amount
              </div>
            </div>

            {sortedData?.map((ele, idx1) => {
              return (
                <>
                  <div className="d-flex">
                    <div style={{ width: "10%" }} className="invoiceBody">
                      {ele["display_booking_id"]}
                    </div>
                    <div
                      style={{ width: "60%", paddingRight: "6rem" }}
                      className="invoiceBody"
                    >
                      {ele["service_name"]}
                      {ele["service_type_name"]
                        ? ` - ${ele["service_type_name"]}`
                        : ""}
                      {" - "}
                      {ele["service_offering_name"]} @{" "}
                      {formatDateDisplay(ele["service_date"])}
                      {" - "}
                      {convertTo12Hour(ele["service_time"])}
                    </div>
                    <div style={{ width: "30%" }} className="invoiceBody">
                      {ele["discount"] ? (
                        <div className="d-flex justify-content-between">
                          <div> Discount:</div>{" "}
                          <div>
                            {" "}
                            {currency} {ele["discount"]}
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}
                      <div className="d-flex justify-content-between">
                        <div> Tax:</div>{" "}
                        <div>
                          {" "}
                          {currency} {ele["tax"]}
                        </div>
                      </div>
                      <div className="d-flex justify-content-between">
                        <div> Amount:</div>{" "}
                        <div>
                          {" "}
                          ${currency} {ele["total"]}
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr />
                </>
              );
            })}

            <div style={{ marginTop: "6rem" }}> </div>
            <footer className="d-flex justify-content-end ">
              <div style={{ width: "30%" }} className="invoiceBody">
                <div className="d-flex justify-content-between">
                  <div> Discount:</div>{" "}
                  <div>
                    {" "}
                    ${currency} {getAmount("discount")}
                  </div>
                </div>

                <div className="d-flex justify-content-between">
                  <div> Tax:</div>{" "}
                  <div>
                    {" "}
                    {currency} {getAmount("tax")}
                  </div>
                </div>
                <div className="d-flex justify-content-between btn-danger p-2 leftRounded">
                  <div> Total Due:</div>{" "}
                  <div>
                    {" "}
                    {currency} {getAmount("total")}
                  </div>
                </div>
              </div>
            </footer>
            <div className="footer-note text-center text-gray-400 text-sm border-t pt-4">
              <p className="font-semibold mb-1">Thank you for your business.</p>
              <p>
                This is a system generated invoice, no signature required for
                the same.
              </p>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <button
            className="btn btn-link text-dark p-0"
            onClick={onBackClick}
            style={{ fontSize: "14px", textDecoration: "none" }}
          >
            <MdArrowBack size={20} /> BACK
          </button>

          {/* <button onClick={downloadPDF}>Download PDF</button> */}

          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <div
                onClick={() => {
                  if (isClinicService) {
                    setAppointmentPanelMode("new");
                    setAppointmentGroupId(null);
                    setShowAppointmentPanel(true);
                  } else {
                    navigate("/vendor_bookings");
                  }
                }}
                className="btn-danger rounded p-2 px-3 cursor-pointer "
              >
                {`New ${getBookingLabel(industry)}`}
              </div>
            </div>
          </div>
        </div>

        {/* ── Filter Bar ── */}
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E3EBF8",
            borderRadius: "12px",
            padding: "16px 20px 4px",
            marginBottom: "16px",
            boxShadow: "0 1px 4px rgba(16,24,40,0.06)",
          }}
        >
          <div className="row mobile-filters">
            {/* Date */}
            <div className="col-6 col-md-4 mb-3">
              <Form.Label
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#6B7280",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                Date Range
              </Form.Label>
              <div
                style={{ position: "relative" }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(!open);
                  }}
                  style={{
                    width: "100%",
                    height: "40px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 12px",
                    fontSize: "13px",
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E3EBF8",
                    color: startDate ? "#111827" : "#9CA3AF",
                    cursor: "pointer",
                    outline: "none",
                    boxShadow: open ? "0 0 0 3px rgba(194,24,91,0.12)" : "none",
                    transition: "box-shadow 0.15s",
                  }}
                >
                  <span>
                    {startDate
                      ? endDate &&
                        new Date(startDate).getTime() !==
                          new Date(endDate).getTime()
                        ? `${formatDateDisplay(
                            startDate
                          )} – ${formatDateDisplay(endDate)}`
                        : formatDateDisplay(startDate)
                      : "Select dates"}
                  </span>
                  <FaRegCalendarAlt
                    style={{ color: "#9CA3AF", flexShrink: 0 }}
                  />
                </button>

                {open && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute",
                      top: "calc(100% + 4px)",
                      left: "0",
                      zIndex: 9999,
                      backgroundColor: "#fff",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      borderRadius: "10px",
                      overflow: "hidden",
                    }}
                  >
                    <DatePicker
                      selectsRange
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(update) => handleDateChange(update)}
                      inline
                      shouldCloseOnSelect={false}
                      {...(isMobile && {
                        renderCustomHeader: ({
                          date,
                          decreaseMonth,
                          increaseMonth,
                        }) => (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "8px 12px",
                            }}
                          >
                            <button onClick={decreaseMonth} style={navBtnStyle}>
                              ‹
                            </button>

                            <div style={{ fontWeight: "600" }}>
                              {date.toLocaleString("default", {
                                month: "long",
                                year: "numeric",
                              })}
                            </div>

                            <button onClick={increaseMonth} style={navBtnStyle}>
                              ›
                            </button>
                          </div>
                        ),
                      })}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Profile */}
            <div className="col-6 col-md-4 mb-3">
              <Form.Label
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#6B7280",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                Profile
              </Form.Label>
              <MultiSelect
                value={selectedPartner}
                onChange={(e) => setSelectedPartner(e.value)}
                options={allPartner}
                optionLabel="name"
                filter
                filterDelay={400}
                placeholder="Select Profile"
                style={{ width: "100%", minHeight: "40px" }}
              />
            </div>

            {/* Crew */}
            <div className="col-6 col-md-4 mb-3">
              <Form.Label
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#6B7280",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                {crewLabel}

              </Form.Label>
              <MultiSelect
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.value)}
                options={allProvider}
                optionLabel="name"
                filter
                filterDelay={400}
                placeholder={`Select ${crewLabel}`}
                style={{ width: "100%", minHeight: "40px" }}
              />
            </div>

            {/* Booking Status */}
            <div className="col-6 col-md-4 mb-3">
              <Form.Label
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#6B7280",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                {bookingStatusLabel}
              </Form.Label>
              <MultiSelect
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.value)}
                options={allStatus}
                optionLabel="name"
                filter
                filterDelay={400}
                placeholder={`Select ${bookingStatusLabel}`}
                style={{ width: "100%", minHeight: "40px" }}
              />
            </div>

            {/* Payment Status */}
            <div className="col-6 col-md-4 mb-3">
              <Form.Label
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#6B7280",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                Payment Status
              </Form.Label>
              <MultiSelect
                value={selectedPayment}
                onChange={(e) => setSelectedPayment(e.value)}
                options={allPayment}
                optionLabel="name"
                filter
                filterDelay={400}
                placeholder="Select Status"
                style={{ width: "100%", minHeight: "40px" }}
              />
            </div>

            {/* Clear All */}
            <div className="col-6 col-md-4 mb-3 d-flex flex-column justify-content-end">
              <span
                className="cursor-pointer danger-underline"
                onClick={() => clearAll()}
                style={{
                  fontSize: "13px",
                  fontWeight: 500,
                  lineHeight: "40px",
                }}
              >
                Clear All
              </span>
            </div>
          </div>
        </div>
<div className="row dashboard-cards g-3 mb-4">
            {(isClinicService
              ? [
                  { label: <strong>Today's Appointment</strong>, value: orderCounts?.total, icon: <LuClock style={{ fontSize: "20px", color: "#037FE8" }} />, bgColor: "#F2F9FF", statusKey: null, color: "#037FE8" },
                  { label: <strong>Check In</strong>, value: orderCounts?.checkedIn, icon: <LuClipboardCheck style={{ fontSize: "20px", color: "#CCCC00" }} />, bgColor: "#FFFFCC7F", statusKey: "checkedin", color: "#CCCC00" },
                  { label: <strong>Check Out</strong>, value: orderCounts?.checkedOut, icon: <HiOutlineUsers style={{ fontSize: "20px", color: "#28A745" }} />, bgColor: "#E6F9F0", statusKey: "checkedout", color: "#28A745" },
                  { label: <strong>No Show</strong>, value: orderCounts?.noShow, icon: <HiOutlineUsers style={{ fontSize: "20px", color: "#FD7E14" }} />, bgColor: "#FFF4E5", statusKey: "noshow", color: "#FD7E14" },
                  { label: <strong>Cancelled</strong>, value: orderCounts?.cancelled, icon: <HiOutlineUsers style={{ fontSize: "20px", color: "#FF0000" }} />, bgColor: "#FDEDED", statusKey: "cancelled", color: "#FF0000" },
                ]
              : [
                  { label: <strong>Total</strong>, value: orderCounts?.total, icon: <LuClock style={{ fontSize: "20px", color: "#037FE8" }} />, bgColor: "#F2F9FF", statusKey: null, color: "#037FE8" },
                  { label: <strong>Completed</strong>, value: orderCounts?.completed, icon: <LuClipboardCheck style={{ fontSize: "20px", color: "#CCCC00" }} />, bgColor: "#FFFFCC7F", statusKey: "completed", color: "#CCCC00" },
                  { label: <strong>In Progress</strong>, value: orderCounts?.progress, icon: <HiOutlineUsers style={{ fontSize: "20px", color: "#92007E" }} />, bgColor: "#FFF0FD", statusKey: "inprogress", color: "#92007E" },
                  { label: <strong>Unassigned</strong>, value: orderCounts?.unassigned, icon: <HiOutlineUsers style={{ fontSize: "20px", color: "#6C757D" }} />, bgColor: "#FFF0FD", statusKey: "unassigned", color: "#6C757D" },
                  { label: <strong>Cancelled</strong>, value: orderCounts?.cancelled, icon: <HiOutlineUsers style={{ fontSize: "20px", color: "#FF0000" }} />, bgColor: "#FDEDED", statusKey: "cancelled", color: "#FF0000" },
                ]
            ).map((item, index) => (
           <div className="dashboard-card-col" key={index}>
              <div
                onClick={() => {
                  // Click-to-filter is clinic-only, per how this was asked
                  // for — other industries' tiles keep their exact prior
                  // (non-interactive) behavior.
                  // (The Unassigned tile also filters for every industry: it is the to-do list for assigning.)
                  if (!isClinicService && item.statusKey !== "unassigned") return;
                  setActiveTileStatus((prev) => (prev === item.statusKey ? null : item.statusKey));
                  setPage(1);
                }}
                className="card d-flex flex-row align-items-center p-3"
                style={{
                  cursor: isClinicService || item.statusKey === "unassigned" ? "pointer" : "default",
                  borderRadius: "12px",
                  height: "90px",
                  border:
                    (isClinicService || item.statusKey === "unassigned") && activeTileStatus === item.statusKey
                      ? `2px solid ${item.color}`
                      : "1px solid #E3EBF8",
                }}
              >
                <div
                  style={{
                    backgroundColor: item.bgColor,
                    borderRadius: "50%",
                    width: "48px",
                    height: "48px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "16px",
                  }}
                >
                  {item.icon}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: `${item.color}`,
                      marginBottom: "4px",
                    }}
                  >
                    {item.label}
                  </div>
                  <h5
                    style={{
                      color: `${item.color}`,
                      fontSize: "18px",
                      fontWeight: "700",
                      margin: 0,
                    }}
                  >
                    {item.value}
                  </h5>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* ── Table action bar ── */}
        <div className="d-flex align-items-center justify-content-between gap-2 mb-2 mobile-actions" style={{ flexWrap: "wrap" }}>
          {/* Search bar */}
          <div style={{ position: "relative", flex: "1 1 260px", maxWidth: "360px" }}>
            <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", fontSize: "15px", pointerEvents: "none" }}>
              🔍
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search by Orgin ID, ${PatientLabel.toLocaleLowerCase()}, mobile, ${crewLabel.toLowerCase()}, ${serviceLabel.toLowerCase()}`}
              style={{
                width: "100%",
                height: "38px",
                paddingLeft: "32px",
                paddingRight: searchQuery ? "32px" : "10px",
                fontSize: "13px",
                borderRadius: "8px",
                border: "1px solid #E3EBF8",
                outline: "none",
                color: "#374151",
                backgroundColor: "#FFFFFF",
                boxSizing: "border-box",
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: "16px", lineHeight: 1, padding: 0 }}
              >
                ×
              </button>
            )}
          </div>

          {/* Export + Download */}
          <div className="d-flex align-items-center justify-content-end flex-wrap gap-2">
            {!cancelMode && (
              <span
                className="d-inline-flex align-items-center gap-1"
                style={{ fontSize: "12px", color: "#6B7280", maxWidth: "260px", lineHeight: 1.3 }}
                data-testid="invoice-hint"
              >
                <MdInfoOutline size={15} style={{ flexShrink: 0 }} aria-hidden="true" />
                Invoices can be generated for completed bookings only.
              </span>
            )}
            <button
              className="btn px-3 d-flex align-items-center gap-2"
              style={{ height: "38px", fontSize: "13px", borderRadius: "8px", backgroundColor: "#FFFFFF", border: "1px solid #E3EBF8", color: "#374151" }}
              onClick={() => exportToCSV()}
            >
              <img src={ExcelIcon} alt="Excel" style={{ width: "18px", height: "18px" }} />
              Export
            </button>

            {cancelMode ? (
              <button
                onClick={() => setShowCancelInvoice(true)}
                style={{ height: "38px", fontSize: "13px", borderRadius: "8px" }}
                className="btn btn-outline-danger px-3"
              >
                Cancel Invoice
              </button>
            ) : (
              <button
                onClick={downloadInv?.length !== 0 ? () => setShowInvoiceDate(true) : passFun}
                style={{ height: "38px", fontSize: "13px", borderRadius: "8px", border: "none", opacity: downloadInv?.length === 0 || mixedSelection ? 0.5 : 1 }}
                className="btn btn-danger px-3"
                disabled={downloadInv?.length === 0 || mixedSelection}
                title={mixedSelection ? "Select either invoiced or not-yet-invoiced bookings, not both" : ""}
              >
                {loading ? "Generating…" : "Download Invoice"}
              </button>
            )}
          </div>
        </div>

        {/* ── Bookings Table ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            overflow: "hidden",
            marginTop: "4px",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: "#fff",
              }}
            >
              <thead>
                <tr>
                  {headers.map((header, idx) => {
                    const colKey = mappingColumns?.[header]?.name;
                    const isSortable = colKey && !nonSortableCols.has(colKey);
                    const thBase = {
                      background: "#F9FAFB",
                      fontWeight: "600",
                      fontSize: "13px",
                      color: "#374151",
                      padding: "12px 16px",
                      borderBottom: "1px solid #E5E7EB",
                      whiteSpace: "nowrap",
                      minWidth: mappingColumns?.[header]?.width || "auto",
                      userSelect: "none",
                    };

                    // S.NO header — contains select-all checkbox
                    if (idx === 0) {
                      return (
                        <th key={idx} style={thBase}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={handleSelectAll}
                              style={{
                                accentColor: "#C0335A",
                                width: "15px",
                                height: "15px",
                                cursor: "pointer",
                              }}
                              title="Select all rows"
                            />
                            {header}
                          </div>
                        </th>
                      );
                    }

                    return (
                      <th
                        key={idx}
                        style={{
                          ...thBase,
                          cursor: isSortable ? "pointer" : "default",
                        }}
                        onClick={() => isSortable && handleSort(colKey)}
                      >
                        {header}
                        {isSortable && (
                          <SortIcon
                            col={colKey}
                            sortCol={sortCol}
                            sortDir={sortDir}
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array(10)
                    .fill(null)
                    .map((_, i) => (
                      <tr key={i}>
                        {headers.map((__, j) => (
                          <td
                            key={j}
                            style={{
                              padding: "12px 16px",
                              borderBottom: "1px solid #F3F4F6",
                            }}
                          >
                            <Skeleton height="16px" marginBottom="0" />
                          </td>
                        ))}
                      </tr>
                    ))
                ) : sortedDisplayData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={headers.length}
                      style={{
                        padding: "48px 16px",
                        textAlign: "center",
                        color: "#6B7280",
                        fontSize: "14px",
                        borderBottom: "1px solid #F3F4F6",
                      }}
                    >
                      {isClinicService ? "No Appointments found" : "No bookings found"}
                    </td>
                  </tr>
                ) : (
                  sortedDisplayData.map((data, index) => (
                    <React.Fragment key={index}>
                    <tr
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#F9FAFB")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = expandedRows.has(data.group_id) ? "#F0F4FF" : "transparent")
                      }
                      style={{
                        transition: "background 0.15s",
                        background: expandedRows.has(data.group_id) ? "#F0F4FF" : "transparent",
                      }}
                    >
                      {/* S.NO + per-row checkbox */}
                      <td
                        style={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#374151",
                          borderBottom: "1px solid #F3F4F6",
                          verticalAlign: "middle",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!data.invoice}
                            onChange={() => checkBoxInvoice(data)}
                            disabled={invoiceBoxLocked(data)}
                            title={
                              isInvoicedRow(data)
                                ? (canCancelInvoices ? "Invoiced - select to cancel the invoice" : "Already invoiced")
                                : !isCompletedStatusName(data.booking_status)
                                ? "An invoice can be generated once the booking is completed"
                                : ""
                            }
                            style={{
                              accentColor: "#C0335A",
                              width: "15px",
                              height: "15px",
                              cursor: invoiceBoxLocked(data) ? "not-allowed" : "pointer",
                              flexShrink: 0,
                              opacity: invoiceBoxLocked(data) ? 0.4 : 1,
                            }}
                          />
                          {index + 1}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleRow(data.group_id); }}
                            title={expandedRows.has(data.group_id) ? "Collapse details" : "Expand details"}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "0 2px",
                              color: expandedRows.has(data.group_id) ? "#C0335A" : "#9CA3AF",
                              fontSize: "13px",
                              lineHeight: 1,
                              flexShrink: 0,
                              transition: "color 0.15s",
                            }}
                          >
                            {expandedRows.has(data.group_id) ? "▲" : "▼"}
                          </button>
                        </div>
                      </td>

                      {headers.map((col, idx) => {
                        if (idx === 0) return null;
                        const key = mappingColumns?.[col]?.name;
                        // A booking made for a family member/dependent has
                        // no `user_name` on the row (only the primary
                        // customer does) — the actual name lives under
                        // `dependent_name`/`first_name` instead, same
                        // fallback chain NewAppointment.jsx's getDataByID
                        // already uses for this exact case. Without it this
                        // column showed "-" even though a name existed.
                        const value =
                          key === "user_name"
                            ? data.user_name || data.dependent_name || data.first_name
                            : data[key];

                        const tdStyle = {
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#374151",
                          borderBottom: "1px solid #F3F4F6",
                          verticalAlign: "middle",
                          whiteSpace: "nowrap",
                        };

                        if (key === "display_booking_id") {
                          return (
                            <td
                              key={idx}
                              style={{
                                ...tdStyle,
                                color: "#C0335A",
                                fontWeight: "600",
                                cursor: "pointer",
                              }}
                              onClick={() => newBookingNavigate(data)}
                            >
                              {value}
                            </td>
                          );
                        }

                        if (key === "invoice_id") {
                          return value ? (
                            <td key={idx} style={tdStyle}>
                              <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href={value}
                                style={{
                                  color: "#C0335A",
                                  fontWeight: "600",
                                  textDecoration: "none",
                                }}
                              >
                                {data.invoice_number || "Download"}
                              </a>
                            </td>
                          ) : (
                            <td
                              key={idx}
                              style={{
                                ...tdStyle,
                                color: "#9CA3AF",
                                fontSize: "12px",
                              }}
                            >
                              —
                            </td>
                          );
                        }

                        if (key === "booking_status") {
                          return (
                            <td key={idx} style={tdStyle}>
                              <StatusPill
                                label={value}
                                configMap={bookingStatusConfig}
                              />
                            </td>
                          );
                        }

                        if (key === "payment_status") {
                          return (
                            <td key={idx} style={tdStyle}>
                              <StatusPill
                                label={value}
                                configMap={paymentStatusConfig}
                              />
                            </td>
                          );
                        }

                        if (key === "service_time") {
                          return (
                            <td key={idx} style={tdStyle}>
                              {value ? convertTo12Hour(value) : "-"}
                            </td>
                          );
                        }

                        if (key === "item_prices") {
                          const names = (data?.item_names || "").split(",");
                          const prices = (data?.item_prices || "").split(",");
                          const combined = names
                            .map((name, i) => {
                              const item = name?.trim();
                              const price = prices?.[i]?.trim();
                              if (!item) return null;
                              return `${item} - ${price || 0}`;
                            })
                            .filter(Boolean);
                          return (
                            <td
                              key={idx}
                              style={{
                                ...tdStyle,
                                maxWidth: "220px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={combined.join(" : ")}
                            >
                              {combined.length ? combined.join(" : ") : "-"}
                            </td>
                          );
                        }

                        const nameKeys = ["user_name", "provider_names"];
                        // On a workflow booking with several people: who is on which step.
                        const onSteps = key === "provider_names" ? formatStepAssignments(data) : null;
                        return (
                          <td key={idx} style={tdStyle} title={onSteps || undefined}>
                            {key === "provider_names" ? (
                              <ProviderAssignCell
                                display={onSteps || (value && !isPlaceholderName(value) ? toTitleCase(value) : "")}
                                isPlaceholder={isPlaceholderName(value)}
                                canAssign={canAssignProviders && canAssignProvider(data)}
                                closed={isClosedBooking(data)}
                                onAssign={() => setAssigning(data)}
                                label={crewLabel.toLowerCase()}
                              />
                            ) : nameKeys.includes(key) ? (
                              value ? toTitleCase(value) : "-"
                            ) : (
                              value ?? "-"
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {expandedRows.has(data.group_id) && (
                      <tr style={{ background: "#F8FAFF" }}>
                        <td
                          colSpan={headers.length}
                          style={{ padding: 0, borderBottom: "2px solid #C0335A" }}
                        >
                          <div
                            style={{
                              padding: "16px 20px",
                              borderLeft: "3px solid #C0335A",
                              display: "flex",
                              gap: "24px",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                            }}
                          >
                            {/* Customer / Patient / Player */}
                            <div style={{ minWidth: "160px", flex: "1" }}>
                              <div style={{ fontSize: "11px", fontWeight: "700", color: "#C0335A", textTransform: "uppercase", marginBottom: "8px", letterSpacing: "0.06em" }}>{isClinicService ? "Patient" : isSportsService ? "Player" : "Customer"}</div>
                              <div style={{ fontSize: "13px", color: "#374151", lineHeight: "1.9" }}>
                                <div><strong>Name:</strong> {data.user_name ? toTitleCase(data.user_name) : "—"}</div>
                                <div><strong>Phone:</strong> {data.phone_number || "—"}</div>
                                {data.email && <div><strong>Email:</strong> {data.email}</div>}
                                {data.org_id && <div><strong>Origin ID:</strong> {data.org_id}</div>}
                              </div>
                            </div>

                            {/* Address */}
                            {(data.house_no || data.area || data.location || data.state) && (
                              <div style={{ minWidth: "160px", flex: "1" }}>
                                <div style={{ fontSize: "11px", fontWeight: "700", color: "#C0335A", textTransform: "uppercase", marginBottom: "8px", letterSpacing: "0.06em" }}>Address</div>
                                <div style={{ fontSize: "13px", color: "#374151", lineHeight: "1.9" }}>
                                  {[data.house_no, data.area, data.location, data.state].filter(Boolean).map((part, i) => (
                                    <div key={i}>{part}</div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Service */}
                            <div style={{ minWidth: "160px", flex: "1" }}>
                              <div style={{ fontSize: "11px", fontWeight: "700", color: "#C0335A", textTransform: "uppercase", marginBottom: "8px", letterSpacing: "0.06em" }}>Service</div>
                              <div style={{ fontSize: "13px", color: "#374151", lineHeight: "1.9" }}>
                                <div><strong>Category:</strong> {data.service_name || "—"}</div>
                                {data.service_type_name && <div><strong>Sub:</strong> {data.service_type_name}</div>}
                                {data.service_offering_name && <div><strong>Type:</strong> {data.service_offering_name}</div>}
                                <div><strong>Date:</strong> {formatDateDisplay(data.service_date)}</div>
                                <div><strong>Time:</strong> {data.service_time ? convertTo12Hour(data.service_time) : "—"}</div>
                                <div>
                                  <strong>{crewLabel}:</strong>{" "}
                                  {canAssignProviders && canAssignProvider(data) ? (
                                    <ProviderAssignCell
                                      display={formatStepAssignments(data) || (realProviderNames(data) ? toTitleCase(realProviderNames(data)) : "")}
                                      canAssign
                                      onAssign={() => setAssigning(data)}
                                      label={crewLabel.toLowerCase()}
                                    />
                                  ) : (
                                    formatStepAssignments(data) || (realProviderNames(data) ? toTitleCase(realProviderNames(data)) : "—")
                                  )}
                                </div>
                                {data.resource_names && <div><strong>{resourceColumnLabel}:</strong> {data.resource_names}</div>}
                                {data.workflow_summary && (
                                  <div>
                                    <strong>Steps:</strong> {data.workflow_summary.done}/{data.workflow_summary.total} done
                                    {data.workflow_summary.unassigned_required > 0 && (
                                      <span style={{ color: "#B45309" }}> · {data.workflow_summary.unassigned_required} need someone assigned</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Financials */}
                            <div style={{ minWidth: "150px", flex: "1" }}>
                              <div style={{ fontSize: "11px", fontWeight: "700", color: "#C0335A", textTransform: "uppercase", marginBottom: "8px", letterSpacing: "0.06em" }}>Financials</div>
                              <div style={{ fontSize: "13px", color: "#374151", lineHeight: "1.9" }}>
                                <div><strong>Total:</strong> {symbol} {formatAmount(data.total)}</div>
                                <div><strong>Discount:</strong> {symbol} {formatAmount(data.discount)}</div>
                                <div><strong>Tax:</strong> {symbol} {formatAmount(data.tax)}</div>
                                <div><strong>Paid:</strong> {symbol} {formatAmount(data.total_paid_amount)}</div>
                                <div style={{ color: Number(data.balance_due) > 0 ? "#D97706" : "#27AE60", fontWeight: "600" }}>
                                  <strong>Balance:</strong> {symbol} {formatAmount(data.balance_due)}
                                </div>
                              </div>
                            </div>

                            {/* Added Items */}
                            {data.item_names && data.item_names.trim() && (
                              <div style={{ minWidth: "160px", flex: "1" }}>
                                <div style={{ fontSize: "11px", fontWeight: "700", color: "#C0335A", textTransform: "uppercase", marginBottom: "8px", letterSpacing: "0.06em" }}>Added Items</div>
                                <div style={{ fontSize: "13px", color: "#374151", lineHeight: "1.9" }}>
                                  {data.item_names.split(",").map((name, i) => {
                                    const price = (data.item_prices || "").split(",")[i];
                                    return name.trim() ? (
                                      <div key={i}>{name.trim()} — {symbol} {formatAmount(price)}</div>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Actions */}
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start", justifyContent: "flex-end", paddingBottom: "4px" }}>
                              {/* <button
                                className="btn btn-danger btn-sm"
                                onClick={() => newBookingNavigate(data.display_booking_id)}
                                style={{ fontSize: "13px", whiteSpace: "nowrap" }}
                              >
                                Edit Booking
                              </button> */}
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => toggleRow(data.group_id)}
                                style={{ fontSize: "12px", border: "1px solid #D1D5DB", background: "#fff", color: "#6B7280", whiteSpace: "nowrap" }}
                              >
                                ▲ Collapse
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 16px",
              flexWrap: "wrap",
              gap: "12px",
              borderTop: "1px solid #F3F4F6",
            }}
          >
            <div style={{ fontSize: "13px", color: "#6B7280" }}>
              {searchFilteredData.length > 0
                ? `Showing ${sortedDisplayData.length} of ${searchFilteredData.length} booking${searchFilteredData.length !== 1 ? "s" : ""}${searchQuery ? " (filtered)" : ""}${downloadInv.length > 0 ? ` · ${downloadInv.length} selected` : ""}`
                : searchQuery ? "No results match your search" : ""}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <select
                value={limit}
                onChange={(e) => {
                  setPage(1);
                  setLimit(Number(e.target.value));
                }}
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: "8px",
                  padding: "6px 10px",
                  fontSize: "13px",
                  color: "#374151",
                  cursor: "pointer",
                }}
              >
                {[3, 10, 20, 50].map((l) => (
                  <option key={l} value={l}>
                    {l} per page
                  </option>
                ))}
              </select>

              <button
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page <= 1}
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: "8px",
                  padding: "6px 14px",
                  fontSize: "13px",
                  background: "#fff",
                  color: page <= 1 ? "#D1D5DB" : "#374151",
                  cursor: page <= 1 ? "not-allowed" : "pointer",
                }}
              >
                Previous
              </button>

              {getPageNumbers().map((num, index) => (
                <button
                  key={index}
                  onClick={() => num !== "..." && setPage(num)}
                  disabled={num === "..."}
                  style={{
                    border: `1px solid ${num === page ? "#C0335A" : "#D1D5DB"}`,
                    borderRadius: "8px",
                    padding: "6px 12px",
                    fontSize: "13px",
                    background: num === page ? "#C0335A" : "#fff",
                    color: num === page ? "#fff" : "#374151",
                    cursor: num === "..." ? "default" : "pointer",
                    fontWeight: num === page ? "600" : "400",
                    minWidth: "36px",
                    pointerEvents: num === "..." ? "none" : "auto",
                  }}
                >
                  {num}
                </button>
              ))}

              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: "8px",
                  padding: "6px 14px",
                  fontSize: "13px",
                  background: "#fff",
                  color: page >= totalPages ? "#D1D5DB" : "#374151",
                  cursor: page >= totalPages ? "not-allowed" : "pointer",
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <Offcanvas
        show={showAppointmentPanel}
        onHide={closeAppointmentPanel}
        placement="end"
        backdrop={true}
        style={{ width: "500px", minWidth: "500px", maxWidth: "500px", flexShrink: 0 }}
      >
        <Offcanvas.Header closeButton className="custom-border-bottom p-4">
          <Offcanvas.Title className="page-heading">
            {appointmentPanelMode === "edit" ? "" : "New Appointment"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0 d-flex flex-column">
          <div className="appointment-card">
            <div className="appointment-body">
              {showAppointmentPanel && (
                <NewAppointment
                  onClose={closeAppointmentPanel}
                  mode={appointmentPanelMode}
                  groupId={appointmentPanelMode === "edit" ? appointmentGroupId : undefined}
                />
              )}
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <AssignProviderModal
        show={!!assigning}
        booking={assigning}
        crewLabel={crewLabel}
        onHide={() => setAssigning(null)}
        onSaved={() => getBookingByDate()}
      />

      <InvoiceCancelDialog
        show={showCancelInvoice}
        onHide={() => setShowCancelInvoice(false)}
        loadImpacts={loadInvoiceImpacts}
        selectedKeys={new Set(invoicedSelected.map(rowKey))}
        noun="booking"
        onConfirm={cancelSelectedInvoices}
      />

      <InvoiceDateDialog
        show={showInvoiceDate}
        onHide={() => setShowInvoiceDate(false)}
        count={downloadInv?.length || 0}
        onConfirm={(invoiceDate) => downloadInvoice(invoiceDate)}
      />
    </>
  );
}

export default DashboardDetailed;

const navBtnStyle = {
  border: "none",
  // background: "#f5f7fa",
  // padding: "6px 10px",
  borderRadius: "6px",
  fontSize: "13px",
};
